// @ts-strict-ignore
import type { FeatureFlag as BrazeFeatureFlag } from "@braze/web-sdk-develop";

import { defaultFeatureFlagDefinitions } from "./constants";
import type { FeatureFlag, FeatureFlagMap, FeatureFlagPropertyTypes } from "./types";

/**
 * Retrieve a feature flag property with runtime validation
 * @param type the feature flag type
 * @param featureFlag the feature flag id
 * @param key the property key
 */
const getProperty = (
  // this fanciness allows passing in any string but still gives us autocomplete for the specific types
  type: FeatureFlagPropertyTypes | (string & { _differentiator?: never }),
  featureFlag: BrazeFeatureFlag,
  key: string
) => {
  switch (type) {
    case "boolean":
      return featureFlag.getBooleanProperty(key);
    case "string":
      return featureFlag.getStringProperty(key);
    case "number":
      return featureFlag.getNumberProperty(key);
    default:
      throw new Error("Unknown feature flag type " + type);
  }
};

/**
 * Convert a feature flag into a simple, type-safe struct with all the properties populated for direct access
 * @param featureFlag a feature flag returned from the Braze web sdk
 */
export const getFeatureFlag = <FlagId extends keyof FeatureFlagMap>(
  featureFlag: BrazeFeatureFlag | undefined
): FeatureFlag<FlagId> => {
  const flagId: FlagId = featureFlag.id as FlagId;
  const propertyMap = defaultFeatureFlagDefinitions?.[flagId]?.properties;
  if (!featureFlag || !propertyMap) {
    return { enabled: false, properties: {} };
  }

  const properties = Object.fromEntries(
    Object.entries(propertyMap).map(([key, type]) => [key, getProperty(typeof type as string, featureFlag, key)])
  ) as FeatureFlag<FlagId>["properties"];
  return {
    enabled: featureFlag.enabled,
    properties,
  };
};
